/**
 * Buscador global de la barra superior — mapa «tipo de CI → módulo donde vive».
 *
 * POR QUÉ ESTÁ AQUÍ
 * ---------------------------------------------------------------------------
 * El backend ya tiene un buscador unificado (`GET /api/ci/search`) que acota
 * por empresa (propios + delegados) y devuelve `{ci_type, ci_id, name, kind…}`.
 * Lo que faltaba era saber a qué pantalla llevar cada resultado y cómo pedirle
 * a esa pantalla que abra la ficha.
 *
 * CONVENCIÓN DEL CMDB PARA ABRIR UNA FICHA DESDE OTRA PANTALLA
 * ---------------------------------------------------------------------------
 *   `?destacar=<id>`          resalta y centra la fila
 *   `?destacar=<id>&abrir=1`  abre además la ficha (en lectura)
 *
 * Casi todos los listados la implementan, así que el buscador global la usa:
 * con un solo clic en un resultado aterrizas en la ficha, no en el listado.
 *
 * Excepciones (no soportan `?destacar`): **racks** y **ubicaciones** usan
 * `navigate(path, { state: { openId } })` (hook `useAutoOpen`), y **circuitos
 * ISP** despliegan el circuito con `?circuit=<id>`.
 *
 * `rack_element` es especial: es UNA tabla con muchos subtipos, cada uno con su
 * listado. Las bandejas y tapas ciegas no tienen listado propio, así que se
 * abren en el visor del rack al que pertenecen (`rack_id` viene del backend).
 */

/** Tipos que se piden al buscador global (solo los que tienen ficha). */
export const GLOBAL_SEARCH_CI_TYPES = [
  // Infraestructura
  'rack_element',
  'chassis',
  'ups',
  'workstation',
  'printer',
  'peripheral',
  'ip_phone',
  'rack',
  'location',
  // Red
  'firewall',
  'router',
  'wifi_controller',
  'wifi_access_point',
  // Virtualización
  'virt_vm',
  'virt_hypervisor',
  'virt_cluster',
  // Servicios y aplicaciones
  'service',
  'service_farm',
  'backup_policy',
  'database_instance',
  'license',
  'web_site',
  // WAN e IPAM
  'isp_circuit',
  'ipam_subnet',
  'ipam_ip',
];

/** Subtipo de `rack_element` → listado que abre su ficha. */
const RACK_ELEMENT_MODULE = {
  server: { path: '/infrastructure/servers', icon: '🖧', labelKey: 'server' },
  switch: { path: '/infrastructure/switches', icon: '🔀', labelKey: 'switch' },
  pdu: { path: '/infrastructure/pdus', icon: '⚡', labelKey: 'pdu' },
  panel: { path: '/infrastructure/patch-panels', icon: '🔌', labelKey: 'panel' },
  storage: { path: '/infrastructure/storage-arrays', icon: '💾', labelKey: 'storage' },
};

/**
 * Resto de tipos: módulo, icono y clave de traducción.
 * `open: 'state'` → `navigate(path, { state: { openId } })`
 * `open: 'query'` → `path + '?circuit=<id>'` (caso especial)
 * por defecto      → `path + '?destacar=<id>&abrir=1'`
 */
export const CI_MODULE = {
  chassis: { path: '/infrastructure/chassis', icon: '📦', labelKey: 'chassis' },
  ups: { path: '/infrastructure/ups', icon: '🔋', labelKey: 'ups' },
  workstation: { path: '/infrastructure/workstations/list', icon: '🖥️', labelKey: 'workstation' },
  printer: { path: '/infrastructure/printers', icon: '🖨️', labelKey: 'printer' },
  peripheral: { path: '/infrastructure/peripherals', icon: '🖱️', labelKey: 'peripheral' },
  ip_phone: { path: '/infrastructure/ip-phones', icon: '☎️', labelKey: 'ip_phone' },
  rack: { path: '/infrastructure/racks', icon: '🗄️', labelKey: 'rack', open: 'state' },
  location: { path: '/locations', icon: '📍', labelKey: 'location', open: 'state' },
  firewall: { path: '/infrastructure/firewalls', icon: '🛡️', labelKey: 'firewall' },
  router: { path: '/wan/routers', icon: '🌐', labelKey: 'router' },
  wifi_controller: { path: '/wifi/controllers', icon: '📡', labelKey: 'wifi_controller' },
  wifi_access_point: { path: '/wifi/aps', icon: '📶', labelKey: 'wifi_access_point' },
  virt_vm: { path: '/virt/vms', icon: '💻', labelKey: 'virt_vm' },
  virt_hypervisor: { path: '/virt/hypervisors', icon: '🧊', labelKey: 'virt_hypervisor' },
  virt_cluster: { path: '/virt/clusters', icon: '🧱', labelKey: 'virt_cluster' },
  service: { path: '/services', icon: '🧩', labelKey: 'service' },
  service_farm: { path: '/services/farms', icon: '🏭', labelKey: 'service_farm' },
  backup_policy: { path: '/backups', icon: '🧰', labelKey: 'backup_policy' },
  database_instance: { path: '/apps/databases', icon: '🗃️', labelKey: 'database_instance' },
  license: { path: '/apps/licenses', icon: '🔑', labelKey: 'license' },
  web_site: { path: '/apps/web-sites', icon: '🌍', labelKey: 'web_site' },
  isp_circuit: { path: '/wan/circuits', icon: '🛰️', labelKey: 'isp_circuit', open: 'circuit' },
  ipam_subnet: { path: '/networks/subnets', icon: '🕸️', labelKey: 'ipam_subnet' },
  ipam_ip: { path: '/networks/ips', icon: '🔢', labelKey: 'ipam_ip' },
};

/**
 * Traduce un resultado del buscador a «a dónde voy y qué le pido».
 *
 * @returns {{path: string, state?: object, icon: string, labelKey: string} | null}
 *          `null` cuando el tipo no tiene ficha (no debería ocurrir: el buscador
 *          global solo pide tipos con destino).
 */
export function searchTarget(result) {
  const ciType = result?.ci_type;
  const ciId = result?.ci_id;
  if (!ciType || ciId == null) return null;

  if (ciType === 'rack_element') {
    const mod = RACK_ELEMENT_MODULE[result.kind];
    if (mod) {
      return {
        path: `${mod.path}?destacar=${ciId}&abrir=1`,
        icon: mod.icon,
        labelKey: mod.labelKey,
      };
    }
    // Bandejas, tapas ciegas y elementos libres: se abren dentro de su rack.
    if (result.rack_id) {
      return {
        path: `/infrastructure/rack/${result.rack_id}?destacar=${ciId}`,
        icon: '🧱',
        labelKey: 'rack_element',
      };
    }
    return null;
  }

  const mod = CI_MODULE[ciType];
  if (!mod) return null;

  if (mod.open === 'state') {
    return { path: mod.path, state: { openId: ciId }, icon: mod.icon, labelKey: mod.labelKey };
  }
  if (mod.open === 'circuit') {
    return { path: `${mod.path}?circuit=${ciId}`, icon: mod.icon, labelKey: mod.labelKey };
  }
  return {
    path: `${mod.path}?destacar=${ciId}&abrir=1`,
    icon: mod.icon,
    labelKey: mod.labelKey,
  };
}

/**
 * Relevancia: primero lo que EMPIEZA por el texto (como en cualquier buscador),
 * después lo que solo lo contiene. Igual en mayúsculas/acentos.
 */
function normalize(text) {
  return String(text || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

export function rankResults(results, query) {
  const q = normalize(query);
  if (!q) return results;
  return [...results].sort((a, b) => {
    const an = normalize(a.name);
    const bn = normalize(b.name);
    const aStarts = an.startsWith(q) ? 0 : 1;
    const bStarts = bn.startsWith(q) ? 0 : 1;
    if (aStarts !== bStarts) return aStarts - bStarts;
    const aWord = an.startsWith(q) || an.includes(` ${q}`) ? 0 : 1;
    const bWord = bn.startsWith(q) || bn.includes(` ${q}`) ? 0 : 1;
    if (aWord !== bWord) return aWord - bWord;
    return an.localeCompare(bn, 'es', { numeric: true });
  });
}
